import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { messagingService } from '@/services/messaging.service';
import type { ListConversationsParams, ListMessagesParams } from '@/services/messaging.service';
import type {
  MessagingConversationView,
  MessagingMessageView,
  MessagingCreateConversationRequest,
  MessagingSendMessageRequest,
  MessagingUnreadCountResponse,
  ApiResponse,
} from '@amuster/types';

// ── Query Keys ──────────────────────────────────────────────────────────────

const KEYS = {
  conversations: ['messaging', 'conversations'] as const,
  messages: (conversationId: string) => ['messaging', 'messages', conversationId] as const,
  unreadCount: ['messaging', 'unread-count'] as const,
  userSearch: (q: string) => ['messaging', 'user-search', q] as const,
};

// ── Conversations (infinite query) ──────────────────────────────────────────

type ConversationsPage = {
  data: MessagingConversationView[];
  meta: { next_cursor: string | null };
};

export function useConversations(params?: Omit<ListConversationsParams, 'cursor'>) {
  return useInfiniteQuery<ConversationsPage>({
    queryKey: [...KEYS.conversations, params],
    queryFn: async ({ pageParam }) => {
      const response = await messagingService.listConversations({
        ...params,
        cursor: pageParam as string | undefined,
      });
      return {
        data: response.data.data ?? [],
        meta: (response.data as unknown as { meta: { next_cursor: string | null } }).meta,
      };
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.meta.next_cursor ?? undefined,
    refetchInterval: 30_000,
  });
}

// ── Messages (infinite query, reverse chronological) ────────────────────────

type MessagesPage = {
  data: MessagingMessageView[];
  meta: { next_cursor: string | null };
};

export function useMessages(
  conversationId: string | null,
  params?: Omit<ListMessagesParams, 'cursor'>,
) {
  return useInfiniteQuery<MessagesPage>({
    queryKey: [...KEYS.messages(conversationId ?? ''), params],
    queryFn: async ({ pageParam }) => {
      const response = await messagingService.listMessages(conversationId!, {
        ...params,
        cursor: pageParam as string | undefined,
      });
      return {
        data: response.data.data ?? [],
        meta: (response.data as unknown as { meta: { next_cursor: string | null } }).meta,
      };
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.meta.next_cursor ?? undefined,
    enabled: !!conversationId,
  });
}

// ── Send Message ────────────────────────────────────────────────────────────

export function useSendMessage() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      data,
    }: {
      conversationId: string;
      data: MessagingSendMessageRequest;
    }) => {
      const response = await messagingService.sendMessage(conversationId, data);
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: KEYS.messages(variables.conversationId) });
      void qc.invalidateQueries({ queryKey: KEYS.conversations });
    },
  });
}

// ── Create Conversation ─────────────────────────────────────────────────────

export function useCreateConversation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (data: MessagingCreateConversationRequest) => {
      const response = await messagingService.createConversation(data);
      return response.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.conversations });
    },
  });
}

// ── Mark Conversation Read ──────────────────────────────────────────────────

export function useMarkConversationRead() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (conversationId: string) => {
      await messagingService.markRead(conversationId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.conversations });
      void qc.invalidateQueries({ queryKey: KEYS.unreadCount });
    },
  });
}

// ── Unread Count ────────────────────────────────────────────────────────────

export function useMessagingUnreadCount() {
  return useQuery<ApiResponse<MessagingUnreadCountResponse>>({
    queryKey: KEYS.unreadCount,
    queryFn: async () => {
      const response = await messagingService.getUnreadCount();
      return response.data;
    },
    refetchInterval: 30_000,
  });
}

// ── User Search ─────────────────────────────────────────────────────────────

export function useMessagingUserSearch(query: string) {
  return useQuery({
    queryKey: KEYS.userSearch(query),
    queryFn: async () => {
      const response = await messagingService.searchUsers(query, 10);
      return response.data.data ?? [];
    },
    enabled: query.length >= 1,
    staleTime: 10_000,
  });
}
